import {
  useOrganisation,
  usePermissions,
  useUpdateOrganisationTmsCopyFunctions,
} from '@my/directory-api/hooks';
import { Button, ErrorBanner, FormSection, Loading, Switch, showToast } from '@my/ui';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  COPY_FUNCTION_ORDER,
  ORDERS_COPY_DEFAULT_ORDER,
  parseCopyFunctions,
  serialiseCopyFunctions,
  type CopyFunctions,
} from '@/features/orders/lib/copyFunctions';

export interface CopyFunctionsSectionProps {
  /** Owning organisation id; the card stays disabled while this is null. */
  organisationId: string | null;
}

/**
 * Which copy functions this organisation is offered — today the "Start from a
 * previous order" section on New order, with more to come.
 *
 * <p>
 * STRATUM SUPPORT ONLY, and it asks here, exactly as `PlanFeaturesSection`
 * does: the endpoint enforces the rule and answers 404 to a customer token, so
 * this is the affordance rather than the defence.
 * </p>
 *
 * <p>
 * Unlike the cards beside it, an unset switch is not "on": each setting shows
 * its own default (see `COPY_FUNCTION_DEFAULTS`) — for the orders copy
 * section, shown.
 * </p>
 */
export function CopyFunctionsSection({ organisationId }: CopyFunctionsSectionProps) {
  const { t } = useTranslation(['configuration', 'common']);
  const { role } = usePermissions();
  const organisationQuery = useOrganisation(organisationId);
  const saveMutation = useUpdateOrganisationTmsCopyFunctions();

  const stored = useMemo(
    () => parseCopyFunctions(organisationQuery.data?.tmsCopyFunctionsJson),
    [organisationQuery.data?.tmsCopyFunctionsJson],
  );

  const [draft, setDraft] = useState<CopyFunctions>(stored);

  // Re-seed whenever the server's copy changes — first load, and after a save
  // invalidates the query. Keyed on the serialised document so a refetch
  // returning identical data does not discard an edit in progress.
  const storedKey = serialiseCopyFunctions(stored);
  useEffect(() => {
    setDraft(parseCopyFunctions(storedKey));
  }, [storedKey]);

  const isDirty = serialiseCopyFunctions(draft) !== storedKey;

  if (role !== 'SuperUser') return null;

  const handleSave = async () => {
    if (!organisationId) return;
    try {
      await saveMutation.mutateAsync({
        organisationId,
        functionsJson: serialiseCopyFunctions(draft),
      });
      showToast({ type: 'success', message: t('configuration:copyFunctions.saved') });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('configuration:copyFunctions.saveFailed'),
      });
    }
  };

  return (
    <FormSection
      title={t('configuration:copyFunctions.title')}
      columns={3}
      disabled={!organisationId}
      footer={
        <Button
          type="button"
          disabled={!isDirty || !organisationId}
          loading={saveMutation.isPending}
          onClick={handleSave}
        >
          {t('common:actions.save')}
        </Button>
      }
    >
      <p className="text-sm text-slate-500 md:col-span-3">
        {t('configuration:copyFunctions.description')}
      </p>

      {organisationQuery.isPending ? (
        <div className="md:col-span-3">
          <Loading label={t('common:states.loading')} />
        </div>
      ) : organisationQuery.isError ? (
        <div className="md:col-span-3">
          <ErrorBanner
            title={t('configuration:copyFunctions.loadFailed')}
            message={organisationQuery.error.message}
          />
        </div>
      ) : (
        <>
          {COPY_FUNCTION_ORDER.map((id) => (
            <Switch
              key={id}
              name={`copyFunction-${id}`}
              label={t(`configuration:copyFunctions.settings.${id}`)}
              checked={draft[id]}
              enabledLabel={t('configuration:copyFunctions.shown')}
              disabledLabel={t('configuration:copyFunctions.hidden')}
              onChange={(e) => setDraft((prev) => ({ ...prev, [id]: e.target.checked }))}
            />
          ))}

          {/* What "Start from a previous order" has ticked when an order is
              picked. The user can still change each one; cargo & service tags
              are always copied, so they have no setting here. */}
          <div className="md:col-span-3">
            <h4 className="text-sm font-medium text-slate-800">
              {t('configuration:copyFunctions.ordersDefaults.title')}
            </h4>
            <p className="text-xs text-slate-500">
              {t('configuration:copyFunctions.ordersDefaults.description')}
            </p>
          </div>
          {ORDERS_COPY_DEFAULT_ORDER.map((id) => (
            <Switch
              key={id}
              name={`copyFunction-${id}`}
              label={t(`configuration:copyFunctions.ordersDefaults.settings.${id}`)}
              checked={draft[id]}
              enabledLabel={t('configuration:copyFunctions.ordersDefaults.ticked')}
              disabledLabel={t('configuration:copyFunctions.ordersDefaults.notTicked')}
              onChange={(e) => setDraft((prev) => ({ ...prev, [id]: e.target.checked }))}
            />
          ))}
          {/* The status a copied order is created with — Draft unless Open is chosen. */}
          <Switch
            name="copyFunction-ordersCopyStartsOpen"
            label={t('configuration:copyFunctions.ordersDefaults.status.label')}
            checked={draft.ordersCopyStartsOpen}
            enabledLabel={t('configuration:copyFunctions.ordersDefaults.status.open')}
            disabledLabel={t('configuration:copyFunctions.ordersDefaults.status.draft')}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, ordersCopyStartsOpen: e.target.checked }))
            }
          />
        </>
      )}
    </FormSection>
  );
}
